import { Button, Card, StatusChip } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ChevronRight, Film, History, Play, RotateCcw, Send, UserRound } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Dialog } from "../../components/Dialog";
import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { api, ApiError, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatBytes, formatDate, formatDateTime } from "../../lib/format";
import { useSession } from "../../lib/session";
import { AssetDrawer, AssetTile, type AssetCard } from "../assets/AssetViews";
import { RelatedFiles } from "../assets/RelatedFiles";
import { UploadDrawer } from "../assets/UploadDrawer";
import { ProductionBoardPage } from "../production/ProductionBoardPage";

type Dashboard = components["schemas"]["EditorDashboardOut"];
type Card_ = components["schemas"]["EditorCardOut"];
type Job = components["schemas"]["EditingJobOut"];
type BriefUpdate = components["schemas"]["BriefUpdateIn"];

const GROUPS = ["due_soon", "in_progress", "revision", "submitted", "approved"] as const;
const A = { start: "startJob", revision: "startRevision", upload: "upload", submit: "submitVersion", brief: "editBrief" } as const;
const OUTCOME_TONE = { UNDER_REVIEW: "review", REVISION_REQUIRED: "warning", APPROVED: "success", REJECTED: "danger" } as const;
const BRIEF = ["target_duration_s", "platform", "aspect_ratio", "hook", "cta", "style_direction", "brief_notes", "revision_limit"] as const;
const NUMERIC = new Set<string>(["target_duration_s", "revision_limit"]);

/** /editor: the Editor Dashboard (doc 04 E1) for Editors; Creative Producers and Leads see the
 *  Production Board filtered to Editing Jobs (plan v2: CP and Creative Lead read). */
export function EditorPage() {
  const me = useSession();
  if (!me.permissions["editing.work"]) {
    return <ProductionBoardPage preset={{ job_type: "EDITING", titleKey: "editor.board_title", subtitleKey: "editor.board_subtitle" }} />;
  }
  return <EditorDashboard />;
}

function EditorDashboard() {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const q = useQuery({ queryKey: ["jobs", "editor"], queryFn: () => call<Dashboard>(api.GET("/app/v1/editor/dashboard")) });
  const first = me.name.split(" ")[0] ?? me.name;
  return (
    <div className="space-y-6">
      <PageHeader titleKey="editor.title" values={{ name: first }} subtitle={t("editor.subtitle")} />
      <QueryState query={q}>
        {(d) => (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
            {GROUPS.map((g) => (
              <section key={g} aria-labelledby={`eg-${g}`} className="flex min-h-48 flex-col rounded-[var(--radius-card)] border border-line bg-elevated p-3">
                <h2 id={`eg-${g}`} className="mb-3 flex items-center justify-between px-1 text-[14px] font-semibold text-ink">
                  {t(`editor.group_${g}`)}<span className="text-[13px] font-normal text-ink-2">{(d.groups[g] ?? []).length}</span>
                </h2>
                <ul className="space-y-3">
                  {(d.groups[g] ?? []).map((c) => <EditorCard key={c.display_id} c={c} lng={i18n.language} tz={me.time_zone} />)}
                </ul>
                {(d.groups[g] ?? []).length === 0 && <p className="px-1 text-[13px] text-ink-2">{t("production.col_empty")}</p>}
              </section>
            ))}
          </div>
        )}
      </QueryState>
    </div>
  );
}

function EditorCard({ c, lng, tz }: { c: Card_; lng: string; tz: string }) {
  const { t } = useTranslation();
  return (
    <li>
      <Link to={`/editor/jobs/${c.display_id}`} className="block rounded-[var(--radius-card)] border border-line bg-surface p-3 text-[13px] shadow-[var(--shadow-card)] hover:border-[var(--border-strong)]">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-ink">{c.display_id}</span>
          {c.current_version > 0 && <span className="text-[11px] text-ink-2">{t("editor.version_short", { n: c.current_version })}</span>}
        </div>
        <div className="mt-1 truncate text-ink">{c.product.name}{c.campaign_id ? ` · ${c.campaign_id}` : ""}</div>
        <div className="truncate text-ink-2">{c.output_type ? t(`plan.type_${c.output_type.toLowerCase()}`) : ""}{c.aspect_ratio ? ` · ${c.aspect_ratio}` : ""}</div>
        <div className="mt-1 flex items-center justify-between gap-2 text-ink-2">
          <span className="flex items-center gap-1"><CalendarClock aria-hidden="true" className="size-3.5" />{c.deadline ? formatDate(c.deadline, lng, tz) : "—"}</span>
          <span>{t(`requests.priority_${c.priority.toLowerCase()}`)}</span>
        </div>
        <div className="mt-1.5 text-[12px] text-violet-text">{t(`status.production_job.${c.status.toLowerCase()}`)}</div>
      </Link>
    </li>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  const rule = error instanceof ApiError ? (error.details.fields as { rule?: string }[] | undefined)?.[0]?.rule : undefined;
  return <p role="alert" className="text-[13px] text-[var(--status-danger)]">{rule ? t(`editor.rule_${rule}`, { defaultValue: t(`error.${code}`) }) : t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

/** Editing Job Detail (doc 04 E2) with Editor Submission (E3). */
export function EditingJobPage() {
  const { displayId = "" } = useParams();
  const { t, i18n } = useTranslation();
  const me = useSession();
  const qc = useQueryClient();
  const key = ["job", displayId, "editing"];
  const q = useQuery({ queryKey: key, queryFn: () => call<Job>(api.GET("/app/v1/editor/jobs/{display_id}", { params: { path: { display_id: displayId } } })) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ["jobs"] }); };
  const start = useMutation({
    mutationFn: (rv: number) => call(api.POST("/app/v1/editor/jobs/{display_id}/start", { params: { path: { display_id: displayId } }, body: { row_version: rv } })),
    onSuccess: refresh,
  });
  const [upload, setUpload] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  return (
    <QueryState query={q}>
      {(j) => {
        const can = (a: string) => j.allowed_actions.includes(a);
        return (
          <div className="space-y-6">
            <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <Link to="/editor" className="hover:text-ink">{t("nav.editor")}</Link>
              <ChevronRight aria-hidden="true" className="size-3.5" />
              <span className="text-ink">{j.display_id}</span>
            </nav>
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <Film aria-hidden="true" className="size-7 text-cyan" />
                  <h1 className="text-[34px] font-semibold tracking-[-0.02em] text-ink">{j.display_id}</h1>
                  <StatusChip tone="editing" icon={null} label={t(`status.production_job.${j.status.toLowerCase()}`)} />
                </div>
                <p className="mt-1 text-[15px] text-ink-2">
                  {j.product.name}{j.campaign_id ? ` · ${j.campaign_id}` : ""} · {j.output_type ? t(`plan.type_${j.output_type.toLowerCase()}`) : t("plan.job_type_editing")}
                  {j.source_shoot && <> · {t("editor.from_shoot")} <Link className="text-violet-text underline" to={`/production/jobs/${j.source_shoot}`}>{j.source_shoot}</Link></>}
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-[14px] text-ink-2">
                  <UserRound aria-hidden="true" className="size-4" />{j.assignee?.name ?? t("requests.unassigned")}
                  {j.deadline && ` · ${t("requests.due_on", { date: formatDate(j.deadline, i18n.language, me.time_zone) })}`}
                  {" · "}{t("editor.revisions_used", { used: j.revisions_used, limit: j.brief.revision_limit })}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {can(A.start) && <Button variant="primary" disabled={start.isPending} onClick={() => start.mutate(j.row_version)}><Play aria-hidden="true" className="size-4" />{t("editor.start_job")}</Button>}
                {can(A.revision) && <Button variant="primary" disabled={start.isPending} onClick={() => start.mutate(j.row_version)}><RotateCcw aria-hidden="true" className="size-4" />{t("editor.start_revision")}</Button>}
                {can(A.upload) && <Button variant="secondary" onClick={() => setUpload(true)}>{t("editor.upload_version")}</Button>}
                {can(A.submit) && <Button variant="primary" disabled={j.drafts.length === 0} onClick={() => setSubmitOpen(true)}><Send aria-hidden="true" className="size-4" />{t("editor.submit_for_review")}</Button>}
                {["SUBMITTED", "REVIEW"].includes(j.status) && j.final_asset_id && (
                  <Link to={`/projects/${j.request}/reviews/${j.display_id}`} className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] border border-line bg-surface px-4 text-sm font-medium text-ink hover:bg-elevated">{t("editor.open_review")}</Link>
                )}
              </div>
            </div>
            <ErrorLine error={start.error} />
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0 space-y-6">
                <Strategy j={j} />
                <Inputs j={j} />
                <Versions j={j} />
              </div>
              <aside className="space-y-6">
                <Brief j={j} onDone={refresh} />
                <RelatedFiles entity={j.display_id} canUpload={can(A.upload) || can(A.brief)}
                  meta={[{ label: t("assets.meta_job"), value: j.display_id }, { label: t("requests.col_product"), value: j.product.name }, { label: t("assets.meta_uploader"), value: me.name }]} />
              </aside>
            </div>
            <UploadDrawer open={upload} onClose={() => setUpload(false)} fixedKind titleKey="editor.upload_title"
              target={{ target_kind: "production_job", target_id: j.display_id }}
              meta={[{ label: t("assets.meta_job"), value: j.display_id }, { label: t("editor.next_version"), value: t("editor.version_short", { n: j.current_version + j.drafts.length + 1 }) }, { label: t("assets.meta_uploader"), value: me.name }]}
              invalidate={[key]} />
            <SubmitDialog j={j} open={submitOpen} onClose={() => setSubmitOpen(false)} onDone={refresh} />
          </div>
        );
      }}
    </QueryState>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="p-6">
      <h2 className="text-[18px] font-semibold text-ink">{title}</h2>
      <div className="mt-3">{children}</div>
    </Card>
  );
}

function Strategy({ j }: { j: Job }) {
  const { t } = useTranslation();
  const s = j.strategy;
  const items: [string, string | null | undefined][] = [
    [t("karya.label_hypothesis"), s.hypothesis], [t("karya.hyp_angle"), s.angle], [t("editor.hook"), s.hook], [t("plan.field_cta"), s.cta],
    [t("editor.duration"), j.brief.target_duration_s ? t("editor.seconds", { n: j.brief.target_duration_s }) : null],
    [t("editor.platform"), j.brief.platform], [t("editor.aspect_ratio"), j.brief.aspect_ratio],
  ];
  return (
    <Section title={t("editor.strategy_summary")}>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-[14px] md:grid-cols-2">
        {items.map(([label, value]) => (
          <div key={label}><dt className="text-[12px] uppercase tracking-wide text-ink-2">{label}</dt><dd className="mt-0.5 text-ink">{value ?? <span className="text-ink-2">{t("requests.not_provided")}</span>}</dd></div>
        ))}
      </dl>
    </Section>
  );
}

function Inputs({ j }: { j: Job }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["job", j.display_id, "assets"],
    queryFn: () => call<AssetCard[]>(api.GET("/app/v1/production/jobs/{display_id}/assets", { params: { path: { display_id: j.display_id } } })) });
  const inputs = (q.data ?? []).filter((a) => a.id !== j.final_asset_id);
  return (
    <Section title={t("editor.assets")}>
      {inputs.length === 0 ? <p className="text-[14px] text-ink-2">{t("editor.no_inputs")}</p> : (
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-3">{inputs.map((a) => <AssetTile key={a.id} a={a} onOpen={setOpen} />)}</ul>
      )}
      <AssetDrawer id={open} onClose={() => setOpen(null)} onOpen={setOpen} />
    </Section>
  );
}

function Versions({ j }: { j: Job }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  return (
    <Section title={t("editor.versions")}>
      {j.submissions.length === 0 && j.drafts.length === 0 && <p className="text-[14px] text-ink-2">{t("editor.no_versions")}</p>}
      <ol className="space-y-3">
        {[...j.submissions].reverse().map((s) => (
          <li key={s.version} className="rounded-[var(--radius-card)] border border-line p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2 font-semibold text-ink"><History aria-hidden="true" className="size-4 text-ink-2" />{t("editor.version_submitted", { n: s.version })}</span>
              <StatusChip tone={OUTCOME_TONE[s.outcome as keyof typeof OUTCOME_TONE] ?? "neutral"} icon={null} label={t(`editor.outcome_${s.outcome.toLowerCase()}`)} />
            </div>
            <p className="mt-1 text-[13px] text-ink-2">
              {s.submitted_by}{s.submitted_at ? ` · ${formatDateTime(new Date(s.submitted_at), i18n.language, me.time_zone)}` : ""}{s.file_name ? ` · ${s.file_name}` : ""}{s.file_size ? ` · ${formatBytes(s.file_size)}` : ""}
            </p>
            {s.notes && <p className="mt-2 whitespace-pre-wrap text-[14px] text-ink">{s.notes}</p>}
            {j.final_asset_id && (
              <a className="mt-2 inline-block text-[13px] text-violet-text underline" href={`/media/assets/${j.final_asset_id}/original?v=${s.version}`} download>{t("editor.download_version", { n: s.version })}</a>
            )}
          </li>
        ))}
        {j.drafts.map((d) => (
          <li key={d.asset_version_id} className="rounded-[var(--radius-card)] border border-dashed border-line p-4 text-[13px]">
            <span className="font-medium text-ink">{t("editor.version_draft", { n: d.version })}</span>
            <span className="text-ink-2"> · {d.file_name} · {formatBytes(d.file_size)}</span>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function Brief({ j, onDone }: { j: Job; onDone: () => void }) {
  const { t } = useTranslation();
  const editable = j.allowed_actions.includes(A.brief);
  const [v, setV] = useState<Record<string, string>>(() => Object.fromEntries(BRIEF.map((k) => [k, j.brief[k] == null ? "" : String(j.brief[k])])));
  const save = useMutation({
    mutationFn: (changes: Partial<BriefUpdate>) => call(api.PATCH("/app/v1/editor/jobs/{display_id}/brief", {
      params: { path: { display_id: j.display_id } }, body: { ...changes, row_version: j.brief.row_version } })),
    onSuccess: onDone,
  });
  const commit = (k: (typeof BRIEF)[number]) => {
    const raw = (v[k] ?? "").trim();
    const value = NUMERIC.has(k) ? (raw ? Number(raw) : null) : raw || null;
    if (value === j.brief[k]) return;
    save.mutate({ [k]: value } as Partial<BriefUpdate>);
  };
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("editor.brief")}</h2>
      <div className="mt-3 space-y-3">
        {BRIEF.map((k) => (
          <div key={k}>
            <label htmlFor={`brief-${k}`} className="mb-1 block text-[12px] font-medium text-ink-2">{t(`editor.field_${k}`)}</label>
            {k === "brief_notes" || k === "style_direction" ? (
              <textarea id={`brief-${k}`} rows={3} disabled={!editable} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} onBlur={() => commit(k)}
                className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-2 text-[13px] text-ink disabled:bg-elevated" />
            ) : (
              <input id={`brief-${k}`} type={NUMERIC.has(k) ? "number" : "text"} disabled={!editable} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} onBlur={() => commit(k)}
                className="h-9 w-full rounded-[var(--radius-control)] border border-line bg-surface px-2 text-[13px] text-ink disabled:bg-elevated" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-2"><ErrorLine error={save.error} /></div>
    </Card>
  );
}

function SubmitDialog({ j, open, onClose, onDone }: { j: Job; open: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const latest = j.drafts[j.drafts.length - 1];
  const [version, setVersion] = useState<string>("");
  const [notes, setNotes] = useState("");
  const chosen = version || latest?.asset_version_id || "";
  const m = useMutation({
    mutationFn: () => call(api.POST("/app/v1/editor/jobs/{display_id}/submit", {
      params: { path: { display_id: j.display_id } }, body: { asset_version_id: chosen, notes: notes.trim() || null, row_version: j.row_version } })),
    onSuccess: () => { setNotes(""); setVersion(""); onClose(); onDone(); },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title={t("editor.submit_title")} description={t("editor.submit_body")}>
      <label htmlFor="submit-version" className="mb-1 block text-[13px] font-medium text-ink-2">{t("editor.version")}</label>
      <select id="submit-version" value={chosen} onChange={(e) => setVersion(e.target.value)} className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink">
        {j.drafts.map((d) => <option key={d.asset_version_id} value={d.asset_version_id}>{t("editor.version_short", { n: d.version })} · {d.file_name}</option>)}
      </select>
      <label htmlFor="submit-notes" className="mb-1 mt-4 block text-[13px] font-medium text-ink-2">{t("editor.notes")}</label>
      <textarea id="submit-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" />
      <div className="mt-2"><ErrorLine error={m.error} /></div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="primary" disabled={!chosen || m.isPending} onClick={() => m.mutate()}>{t("editor.submit_for_review")}</Button>
      </div>
    </Dialog>
  );
}
